import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { KDialog, KCropperWrapper, KTextInput } from "@kyburz/kdesign-system";
import { Grid, Divider } from "@material-ui/core";
import useSetState from "../../hooks/useSetState";
import { updateVehicleTypes } from "../../apiCalls/admin/vehicleTypes";
import { setAlert } from "../../redux/actions/actions";
import { getVehicleUrl } from "../../apiCalls/imageUploads/vehicleUploads";
import { RootStateInterface } from "../../interfaces/ReduxInterface";
import { humanize } from "../../utils/utils";

interface Props {
  open: boolean;
  handleClose: (boolean) => void;
  vehicleType: string | null;
  skeletonUrl: string | null;
  icon_3dUrl: string | null;
  icon_2dUrl: string | null;
  label: Record<string, string>;
}

const NewVehicleType = ({
  open,
  handleClose,
  vehicleType,
  skeletonUrl,
  icon_3dUrl,
  icon_2dUrl,
  label,
}: Props) => {
  const currentCustomer = useSelector(
    (state: RootStateInterface) => state.customer.currentCustomer,
  );
  const dispatch = useDispatch();
  const [{ type, skeleton, icon_3d, icon_2d, labels }, setState] = useSetState({
    type: vehicleType,
    skeleton: skeletonUrl,
    icon_3d: icon_3dUrl,
    icon_2d: icon_2dUrl,
    labels: label,
  });

  const setIcon = (name, url) => {
    setState({ [name]: url });
  };

  const handleCreateType = (): void => {
    const body = { name: type, skeleton, icon_3d, icon_2d, label: labels };
    updateVehicleTypes(currentCustomer.shortname, body, type).then((res) => {
      if (res.data) {
        handleClose(true);
        dispatch(setAlert("Vehicle Type successfully updated", "success"));
      } else {
        dispatch(setAlert(JSON.stringify(res.detail.errors), "error"));
      }
    });
  };

  const onLabelChange = (e) => {
    setState({
      labels: {
        ...labels,
        [e.target.name]: e.target.value,
      }
    })
  };

  const renderLabels = Object.keys(labels).map((l) => {
    const inputLabel = `${humanize(l)} Label`;
    return (
      <Grid item xs={12} md={3}>
        <KTextInput
          placeholder={vehicleType || inputLabel}
          value={labels[l]}
          onChange={onLabelChange}
          name={l}
          label={inputLabel}
          width="200px"
        />
      </Grid>
  )});

  return (
    <KDialog
      open={open}
      title="Edit Vehicle Type"
      size="md"
      onClose={() => handleClose(false)}
      primaryCTAonClick={handleCreateType}
      primaryCTAText="Update"
      primaryCTAColor="primary"
      secondaryCTAonClick={() => handleClose(false)}
      secondaryCTAText="Cancel"
      hasOverflow
      disabled={type === "" || skeleton === ""}
    >
      <KCropperWrapper
        title="Skeleton"
        name="skeleton"
        uploadText="Choose file..."
        helperText="Allowed files are .PNG, .JPG and .JPEG"
        uploadPosition="right"
        width={400}
        height={400}
        borderRadius={5}
        fetchUrl={getVehicleUrl}
        uploadUrl={`${currentCustomer.shortname}/vehicles/upload_image`}
        style={{ width: 400, height: 400 }}
        setAvatar={setIcon}
        image={skeleton}
      />
      <Divider />
      <Grid
        container
        spacing={3}
        style={{ marginRight: 50 }}
        justify="space-between"
      >
        <Grid item xs={12} md={6}>
          <KCropperWrapper
            title="Icon 2D "
            name="icon_2d"
            helperText="Allowed files are .PNG"
            uploadPosition="right"
            width={50}
            height={50}
            borderRadius={5}
            fetchUrl={getVehicleUrl}
            uploadUrl={`${currentCustomer.shortname}/vehicles/upload_image`}
            style={{ width: 50, height: 50 }}
            setAvatar={setIcon}
            image={icon_2d}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <KCropperWrapper
            title="Icon 3D "
            name="icon_3d"
            helperText="Allowed files are .PNG"
            uploadPosition="right"
            width={50}
            height={50}
            borderRadius={5}
            fetchUrl={getVehicleUrl}
            uploadUrl={`${currentCustomer.shortname}/vehicles/upload_image`}
            style={{ width: 50, height: 50 }}
            setAvatar={setIcon}
            image={icon_3d}
          />
        </Grid>
      </Grid>
      <Divider />
      <Grid
        container
        spacing={1}
        justify="space-between"
      >
        {renderLabels}
      </Grid>
    </KDialog>
  );
};

export default NewVehicleType;
